import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  signal,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { EdeskService, DraftListResponse, DraftSummaryDto } from '../../core/edesk.service';

@Component({
  selector: 'app-draft-list',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './draft-list.component.html',
})
export class DraftListComponent implements OnInit {
  private edeskService = inject(EdeskService);
  
  @Input() pageSize = 10;
  @Output() draftSelected = new EventEmitter<number>();
  
  private drafts = signal<DraftSummaryDto[]>([]);
  loading = signal(false);
  error = signal<string | null>(null);
  
  private total = 0;
  private pageIndex = signal(0);
  private requestSeq = 0;

  ngOnInit(): void {
    this.loadDrafts();
  }

  loadDrafts(): void {
    const seq = ++this.requestSeq;
    this.loading.set(true);
    this.error.set(null);

    const skip = this.pageIndex() * this.pageSize;

    this.edeskService.getDraftList(this.pageSize, skip).subscribe({
      next: (response: DraftListResponse) => {
        if (seq !== this.requestSeq) return;
        
        this.drafts.set(response.drafts || []);
        this.total = response.totalCount || 0;
        this.loading.set(false);
      },
      error: (err) => {
        if (seq !== this.requestSeq) return;
        console.error('Failed to load drafts:', err);
        this.error.set('Nepodarilo sa načítať rozpracované správy.');
        this.loading.set(false);
      }
    });
  }

  selectDraft(draft: DraftSummaryDto): void {
    if (draft.draftId) {
      this.draftSelected.emit(draft.draftId);
    }
  }

  formatDate(dateString: string | undefined): string {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('sk-SK', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '';
    }
  }

  formatRecipient(uri: string | undefined): string {
    if (!uri) return '(Bez príjemcu)';
    const parts = uri.split('/');
    return parts[parts.length - 1] || uri;
  }

  refresh(): void {
    this.pageIndex.set(0);
    this.loadDrafts();
  }

  // Pagination helpers
  headers(): DraftSummaryDto[] {
    return this.drafts();
  }

  page(): number {
    return this.pageIndex() + 1;
  }

  pages(): number {
    return Math.max(1, Math.ceil(this.total / this.pageSize));
  }

  canPrev(): boolean {
    return this.pageIndex() > 0;
  }

  canNext(): boolean {
    return this.pageIndex() + 1 < this.pages();
  }

  canGoFirst(): boolean {
    return this.page() > 1;
  }

  canGoLast(): boolean {
    return this.page() < this.pages();
  }

  prevPage(): void {
    if (!this.canPrev()) return;
    this.pageIndex.update(i => i - 1);
    this.loadDrafts();
  }

  nextPage(): void {
    if (!this.canNext()) return;
    this.pageIndex.update(i => i + 1);
    this.loadDrafts();
  }

  firstPage(): void {
    this.gotoPage(1);
  }

  lastPage(): void {
    this.gotoPage(this.pages());
  }

  gotoPage(p: number): void {
    const idx = p - 1;
    if (idx < 0 || idx >= this.pages()) return;
    this.pageIndex.set(idx);
    this.loadDrafts();
  }

  pagesArray(): (number | string)[] {
    const totalPages = this.pages();
    const cur = this.page();
    
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    
    const pages: (number | string)[] = [];
    pages.push(1);
    if (totalPages > 1) pages.push(2);
    
    if (cur > 4) {
      pages.push('...');
    }
    
    for (let i = Math.max(3, cur - 1); i <= Math.min(totalPages - 2, cur + 1); i++) {
      if (!pages.includes(i)) {
        pages.push(i);
      }
    }
    
    if (cur < totalPages - 3) {
      pages.push('...');
    }
    
    if (totalPages > 2 && !pages.includes(totalPages - 1)) {
      pages.push(totalPages - 1);
    }
    if (!pages.includes(totalPages)) {
      pages.push(totalPages);
    }
    
    return pages;
  }

  rangeStart(): number {
    if (this.total === 0) return 0;
    return this.pageIndex() * this.pageSize + 1;
  }

  rangeEnd(): number {
    return Math.min((this.pageIndex() + 1) * this.pageSize, this.total);
  }

  totalCount(): number {
    return this.total;
  }
}
